/**
 * Capturing a receipt on the phone, typically at the restaurant table: photograph it (several
 * photos for a long one), say whether it was a business meal, whom it was with and what tip was
 * added, done. Each photo becomes its scan right here - the receipt alone, straightened, black and
 * white - plus a compressed copy (lib/scan/photo.ts); the server only reads them. Anything waits
 * on the phone while there is no connection (see lib/capture/capture-queue.ts).
 */
import { Camera, CheckCircle2, CloudOff, ImagePlus, Loader2, TriangleAlert, X } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "sonner";
import { BelegNumber } from "@/components/documents/document-badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { CaptureCropDialog } from "@/components/capture/capture-crop-dialog";
import { useCaptureQueue } from "@/hooks/use-capture-queue";
import { scanPdf, scanPhoto, type ScannedPhoto } from "@/lib/scan/photo";
import { useCategories } from "@/hooks/use-documents";
import type { CaptureKind } from "@/lib/capture/capture-queue";
import { useAuth } from "@/lib/session/auth-context";
import { ownNames } from "@/lib/api/extract";
import { formatMoney, parseDecimal } from "@/lib/utils/format";
import { cn } from "@/lib/utils/cn";
import { useWorkspace } from "@/lib/session/workspace-context";

/** Warnings worth acting on while still at the table: the photo can be taken again. */
const PHOTO_WARNINGS = ["scan_low_resolution", "scan_blurry", "scan_not_found"] as const;
type PhotoWarning = (typeof PHOTO_WARNINGS)[number];
const isPhotoWarning = (value: string): value is PhotoWarning =>
  (PHOTO_WARNINGS as readonly string[]).includes(value);

/** "Beleg-2026-10-03-2041-2.jpg": a name that still says something in the documents list. */
function captureName(index: number, at: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  const stamp = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}-${pad(at.getHours())}${pad(at.getMinutes())}`;
  return `Beleg-${stamp}${index > 0 ? `-${index + 1}` : ""}.jpg`;
}

/** A photo as taken on this page: scanned on the phone, or as it is when the phone cannot decode it. */
type Photo = ScannedPhoto | { original: File; scan?: undefined; page?: undefined };

function Thumbnail({
  file,
  label,
  onOpen,
  onRemove,
}: {
  file: File;
  label: string;
  /** Opens the crop; absent for a photo the phone could not scan. */
  onOpen?: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  // Each <img> gets its own object URL, released once it has loaded. A URL kept in a memo would
  // be revoked by the effect cleanup React runs on remount (StrictMode) and leave a broken image.
  const show = useCallback(
    (img: HTMLImageElement | null) => {
      if (!img) return;
      const url = URL.createObjectURL(file);
      img.onload = () => URL.revokeObjectURL(url);
      img.src = url;
    },
    [file],
  );
  return (
    <div
      className="relative aspect-[3/4] overflow-hidden rounded-md border bg-muted"
      data-testid="capture-page"
    >
      {/* The scan, whole: a long receipt is narrow, and the crop is what is being checked. */}
      <img ref={show} alt={label} className="size-full bg-white object-contain" />
      {onOpen && (
        <button
          type="button"
          onClick={onOpen}
          aria-label={`${t("crop.open")} · ${label}`}
          className="absolute inset-0"
          data-testid="capture-page-crop"
        />
      )}
      <span className="absolute bottom-1 left-1 rounded bg-background/90 px-1.5 text-xs num">
        {label}
      </span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={label}
        className="absolute top-1 right-1 rounded-full bg-background/90 p-1"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}

export function CapturePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { workspace, canWrite } = useWorkspace();
  const { data: categories } = useCategories(workspace?.id);
  const saving = useRef(false);
  const [savingNow, setSavingNow] = useState(false);
  const { waiting, sent, sending, enqueue, discard } = useCaptureQueue({
    userId: user?.id ?? "",
    ownVatId: workspace?.vatId || undefined,
    ownIban: workspace?.iban || undefined,
    ownNames: ownNames(workspace) || undefined,
  });

  const input = useRef<HTMLInputElement>(null);
  const [pages, setPages] = useState<Photo[]>([]);
  const [scanning, setScanning] = useState(0);
  /** The page whose crop is being checked: each new photo opens it, a tap on a page too. */
  const [editing, setEditing] = useState<number | null>(null);
  const [kind, setKind] = useState<CaptureKind>("KA");
  const [entertainment, setEntertainment] = useState(false);
  const [notes, setNotes] = useState("");
  const [tip, setTip] = useState("");

  // Bewirtungsspesen, account 7680 in the chart every business starts with.
  const entertainmentCategory = categories?.find(
    (category) => category.kind === "expense" && category.accountNumber === "7680",
  );

  if (!canWrite) return <p className="text-muted-foreground">{t("capture.readOnly")}</p>;

  const addPhotos = async (files: File[]) => {
    // `files` is a copy: the input is cleared right after, which empties its live FileList.
    if (files.length === 0) return;
    const at = new Date();
    // The bytes, read right away: iOS may no longer let the app read the camera's file later (after
    // switching apps, or offline), and the queue stores bytes anyway (capture-queue.ts).
    let photos: Blob[];
    try {
      photos = await Promise.all(
        files.map(async (file) => new Blob([await file.arrayBuffer()], { type: file.type })),
      );
    } catch {
      toast.error(t("capture.unreadablePhoto"));
      return;
    }
    setScanning((count) => count + photos.length);
    const first = pages.length;
    let check: number | null = null;
    for (const [index, photo] of photos.entries()) {
      const name = captureName(first + index, at);
      let page: Photo;
      try {
        page = await scanPhoto(photo, name);
        check ??= first + index;
      } catch {
        // Not decodable here (rare on a phone): sent as it is, services/ai scans it.
        page = { original: new File([photo], name, { type: photo.type || "image/jpeg" }) };
      }
      setPages((current) => [...current, page]);
      setScanning((count) => count - 1);
    }
    // Take a photo, check the crop, save: the crop opens for the first new page.
    setEditing(check);
  };

  const save = async () => {
    // A second tap while the scan PDF is being built would queue the receipt twice (QA-0011);
    // the ref holds before React re-renders the disabled button.
    if (!workspace || pages.length === 0 || saving.current) return;
    saving.current = true;
    setSavingNow(true);
    try {
      await queue();
    } finally {
      saving.current = false;
      setSavingNow(false);
    }
  };

  const queue = async () => {
    if (!workspace) return;
    const tipValue = parseDecimal(tip);
    // One photo the phone could not scan, and services/ai scans them all, as one PDF.
    const scanned = pages.every((page) => page.scan) ? (pages as ScannedPhoto[]) : null;
    await enqueue({
      workspaceId: workspace.id,
      kind,
      pages: pages.map((page) => page.original),
      ...(scanned
        ? {
            scan: {
              pdf: await scanPdf(scanned, scanned[0]!.original.name),
              pages: scanned.map((page) => page.page),
            },
          }
        : {}),
      ...(entertainment && entertainmentCategory
        ? {
            category: entertainmentCategory.id,
            deductiblePercent: entertainmentCategory.deductiblePercent ?? 50,
          }
        : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
      ...(tipValue && tipValue > 0 ? { tip: tipValue } : {}),
    });
    toast.success(navigator.onLine ? t("capture.queued") : t("capture.queuedOffline"));
    setPages([]);
    setNotes("");
    setTip("");
    setEntertainment(false);
  };

  return (
    <div className="mx-auto max-w-md space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">{t("capture.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("capture.subtitle")}</p>
      </div>

      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        data-testid="capture-input"
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          event.target.value = "";
          void addPhotos(files);
        }}
      />

      <CaptureCropDialog
        photo={editing !== null && pages[editing]?.scan ? (pages[editing] as ScannedPhoto) : null}
        number={(editing ?? 0) + 1}
        onApply={(photo) => {
          setPages((current) => current.map((page, at) => (at === editing ? photo : page)));
          setEditing(null);
        }}
        onClose={() => setEditing(null)}
      />

      {pages.length === 0 && scanning === 0 ? (
        <button
          type="button"
          onClick={() => input.current?.click()}
          className="flex h-44 w-full flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed bg-muted/40 text-base font-medium transition-colors hover:bg-muted"
          data-testid="capture-take"
        >
          <Camera className="size-9" />
          {t("capture.take")}
        </button>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {pages.map((page, index) => (
            <Thumbnail
              key={`${page.original.name}-${index}`}
              file={page.scan ?? page.original}
              label={t("capture.page", { number: index + 1 })}
              onOpen={page.scan ? () => setEditing(index) : undefined}
              onRemove={() => setPages((current) => current.filter((_, at) => at !== index))}
            />
          ))}
          {Array.from({ length: scanning }, (_, index) => (
            <div
              key={`scanning-${index}`}
              className="flex aspect-[3/4] flex-col items-center justify-center gap-1 rounded-md border bg-muted text-xs text-muted-foreground"
              data-testid="capture-scanning"
            >
              <Loader2 className="size-5 animate-spin" />
              {t("capture.scanning")}
            </div>
          ))}
          <button
            type="button"
            onClick={() => input.current?.click()}
            className="flex aspect-[3/4] flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed text-xs text-muted-foreground hover:bg-muted"
            data-testid="capture-add-page"
          >
            <ImagePlus className="size-5" />
            {t("capture.addPage")}
          </button>
        </div>
      )}
      {pages.some((page) => page.page && !page.page.detected) && (
        <p className="text-xs text-muted-foreground" data-testid="capture-edges-missed">
          {t("capture.edgesMissed")}
        </p>
      )}
      {pages.length > 0 && (
        <p className="text-xs text-muted-foreground">{t("capture.pagesHint")}</p>
      )}

      <div className="space-y-2">
        <Label>{t("capture.kind")}</Label>
        <div className="grid grid-cols-2 gap-2" role="radiogroup">
          {(["KA", "ER"] as const).map((value) => (
            <Button
              key={value}
              type="button"
              role="radio"
              aria-checked={kind === value}
              variant={kind === value ? "default" : "outline"}
              onClick={() => setKind(value)}
              data-testid={`capture-kind-${value}`}
            >
              {t(`capture.kinds.${value}`)}
            </Button>
          ))}
        </div>
      </div>

      <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
        <div className="space-y-0.5">
          <Label htmlFor="capture-entertainment">{t("capture.entertainment")}</Label>
          <p className="text-xs text-muted-foreground">{t("capture.entertainmentHint")}</p>
        </div>
        <Switch
          id="capture-entertainment"
          checked={entertainment}
          onCheckedChange={setEntertainment}
          disabled={!entertainmentCategory}
          data-testid="capture-entertainment"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="capture-notes">
          {entertainment ? t("capture.guests") : t("capture.notes")}
        </Label>
        <Textarea
          id="capture-notes"
          rows={2}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder={entertainment ? t("capture.guestsPlaceholder") : undefined}
          data-testid="capture-notes"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="capture-tip">{t("capture.tip")}</Label>
        <Input
          id="capture-tip"
          inputMode="decimal"
          className="max-w-40 text-right num"
          placeholder="0,00"
          value={tip}
          onChange={(event) => setTip(event.target.value)}
          data-testid="capture-tip"
        />
        <p className="text-xs text-muted-foreground">{t("capture.tipHint")}</p>
      </div>

      <Button
        type="button"
        size="lg"
        className="w-full"
        disabled={pages.length === 0 || scanning > 0 || savingNow}
        onClick={() => void save()}
        data-testid="capture-save"
      >
        {t("capture.save")}
      </Button>

      {waiting.length > 0 && (
        <section className="space-y-2" data-testid="capture-waiting">
          <h2 className="flex items-center gap-2 text-sm font-medium">
            {sending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <CloudOff className="size-4" />
            )}
            {sending ? t("capture.sending") : t("capture.waiting", { count: waiting.length })}
          </h2>
          <ul className="space-y-1 text-sm text-muted-foreground">
            {waiting.map((item) => (
              <li key={item.id} data-testid="capture-waiting-item">
                {t(`capture.kinds.${item.kind}`)} ·{" "}
                {t("capture.pages", { count: item.pages.length })}
                {item.error && (
                  <span className="flex flex-wrap items-center gap-x-3 text-destructive">
                    <span className="min-w-0">
                      {item.error === "unreadable" ? t("capture.unreadable") : item.error}
                    </span>
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto p-0"
                      onClick={() => void discard(item.id)}
                      data-testid="capture-discard"
                    >
                      {t("capture.discard")}
                    </Button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {sent.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-medium">{t("capture.sent")}</h2>
          <ul className="space-y-2">
            {sent.map(({ id, outcome }) => {
              const photoWarnings = outcome.warnings.filter(isPhotoWarning);
              return (
                <li key={id} className="rounded-lg border p-3 text-sm" data-testid="capture-sent">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 shrink-0 text-success" />
                    <Link to={`/documents/${outcome.documentId}`} className="font-medium">
                      {outcome.docNumber ? (
                        <BelegNumber value={outcome.docNumber} />
                      ) : (
                        t("capture.open")
                      )}
                    </Link>
                    <span className="ml-auto num">
                      {outcome.grossTotal ? formatMoney(outcome.grossTotal, "EUR") : ""}
                    </span>
                  </div>
                  {!outcome.extracted && (
                    <p className="mt-1 text-xs text-muted-foreground">{t("capture.notRead")}</p>
                  )}
                  {photoWarnings.map((warning) => (
                    <p key={warning} className={cn("mt-1 flex gap-1.5 text-xs text-warning")}>
                      <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
                      {t(`extraction.warnings.${warning}`)}
                    </p>
                  ))}
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
}
